import { useState } from "react";
import {
  Plus,
  Search,
  UserCog,
  Key,
  MoreHorizontal,
  Hash,
  Lock,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import {
  useUsers,
  useRoles,
  useCreateUser,
  useUpdateUser,
  useChangePassword,
  useToggleUserActive,
  useSetUserPin,
  useResetPinAttempts,
} from "../api";
import { useAuthStore } from "@/stores/auth.store";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@/components/ui";
import {
  PageHeader,
  PageLoader,
  PageError,
  EmptyState,
  PermissionButton,
  PermissionGate,
  DataTable,
  type Column,
} from "@/components/common";
import type { User, CreateUserRequest, UpdateUserRequest } from "@/types";

const roleColors: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  admin: "destructive",
  manager: "default",
  barista: "secondary",
  cashier: "outline",
};

function PinStatusBadge({ user }: { user: User }) {
  if (!user.hasPin) return null;

  if (user.pinLocked) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-red-300 bg-red-50 text-red-700"
        title="PIN login is locked. Reset attempts to re-enable."
      >
        <Lock className="h-3 w-3" />
        PIN locked
      </Badge>
    );
  }

  if (user.pinAttemptsRemaining < user.pinMaxAttempts) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-300 bg-amber-50 text-amber-700"
        title={`${user.pinAttemptsRemaining} of ${user.pinMaxAttempts} PIN attempts remaining`}
      >
        <Lock className="h-3 w-3" />
        {user.pinAttemptsRemaining}/{user.pinMaxAttempts} tries
      </Badge>
    );
  }

  return null;
}

export function UsersPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isPinOpen, setIsPinOpen] = useState(false);

  const currentUser = useAuthStore((state) => state.user);
  const {
    data: usersData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUsers({
    search: search || undefined,
    page,
    limit: pageSize,
  });
  const { data: roles } = useRoles();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const changePassword = useChangePassword();
  const setUserPin = useSetUserPin();
  const toggleActive = useToggleUserActive();
  const resetPinAttempts = useResetPinAttempts();

  const users = usersData?.data ?? [];
  const pagination = usersData?.pagination;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleCreate = async (data: Partial<CreateUserRequest>) => {
    try {
      await createUser.mutateAsync(data as CreateUserRequest);
      toast.success("User created successfully");
      setIsCreateOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create user");
    }
  };

  const handleUpdate = async (data: Partial<CreateUserRequest>) => {
    if (!selectedUser) return;
    try {
      await updateUser.mutateAsync({
        id: selectedUser.id,
        data: data as UpdateUserRequest,
      });
      toast.success("User updated successfully");
      setIsEditOpen(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update user");
    }
  };

  const handleChangePassword = async (password: string) => {
    if (!selectedUser) return;
    try {
      await changePassword.mutateAsync({
        id: selectedUser.id,
        data: { password },
      });
      toast.success("Password changed successfully");
      setIsPasswordOpen(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to change password",
      );
    }
  };

  const handleSetPin = async (pin: string) => {
    if (!selectedUser) return;
    try {
      await setUserPin.mutateAsync({ id: selectedUser.id, data: { pin } });
      toast.success("PIN set successfully");
      setIsPinOpen(false);
      setSelectedUser(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to set PIN");
    }
  };

  const handleResetPinAttempts = async (user: User) => {
    try {
      await resetPinAttempts.mutateAsync(user.id);
      toast.success(`PIN lockout cleared for ${user.firstName} ${user.lastName}`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to reset PIN attempts",
      );
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await toggleActive.mutateAsync({ id: user.id, isActive: !user.isActive });
      toast.success(
        `User ${user.isActive ? "deactivated" : "activated"} successfully`,
      );
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to update user status",
      );
    }
  };

  const columns: Column<User>[] = [
    {
      key: "name",
      header: "Name",
      cell: (user) => (
        <span className="font-medium">
          {user.firstName} {user.lastName}
        </span>
      ),
    },
    {
      key: "username",
      header: "Username",
      cell: (user) => user.username,
    },
    {
      key: "email",
      header: "Email",
      cell: (user) => user.email,
    },
    {
      key: "role",
      header: "Role",
      cell: (user) => (
        <Badge variant={roleColors[user.role?.name] || "secondary"}>
          {user.role?.name || "Unknown"}
        </Badge>
      ),
    },
    {
      key: "pin",
      header: "PIN",
      cell: (user) =>
        !user.hasPin ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : user.pinLocked || user.pinAttemptsRemaining < user.pinMaxAttempts ? (
          <PinStatusBadge user={user} />
        ) : (
          <span className="text-xs text-muted-foreground">OK</span>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (user) => (
        <Switch
          checked={user.isActive}
          onCheckedChange={() => handleToggleActive(user)}
          disabled={user.id === currentUser?.id}
          title={
            user.id === currentUser?.id
              ? "Cannot deactivate yourself"
              : undefined
          }
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "w-[100px]",
      cell: (user) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setSelectedUser(user);
                setIsEditOpen(true);
              }}
            >
              <UserCog className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSelectedUser(user);
                setIsPasswordOpen(true);
              }}
            >
              <Key className="mr-2 h-4 w-4" />
              Change Password
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSelectedUser(user);
                setIsPinOpen(true);
              }}
            >
              <Hash className="mr-2 h-4 w-4" />
              Set PIN
            </DropdownMenuItem>
            {user.hasPin && user.pinAttemptsRemaining < user.pinMaxAttempts && (
              <DropdownMenuItem
                onClick={() => handleResetPinAttempts(user)}
                disabled={resetPinAttempts.isPending}
              >
                <Unlock className="mr-2 h-4 w-4" />
                Reset PIN attempts
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const renderMobileCard = (user: User) => (
    <div className="rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-sm text-muted-foreground">@{user.username}</p>
          <p className="truncate text-sm text-muted-foreground">{user.email}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <PermissionGate permission="users.update">
              <DropdownMenuItem
                onClick={() => {
                  setSelectedUser(user);
                  setIsEditOpen(true);
                }}
              >
                <UserCog className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedUser(user);
                  setIsPasswordOpen(true);
                }}
              >
                <Key className="mr-2 h-4 w-4" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedUser(user);
                  setIsPinOpen(true);
                }}
              >
                <Hash className="mr-2 h-4 w-4" />
                Set PIN
              </DropdownMenuItem>
              {user.hasPin && user.pinAttemptsRemaining < user.pinMaxAttempts && (
                <DropdownMenuItem
                  onClick={() => handleResetPinAttempts(user)}
                  disabled={resetPinAttempts.isPending}
                >
                  <Unlock className="mr-2 h-4 w-4" />
                  Reset PIN attempts
                </DropdownMenuItem>
              )}
            </PermissionGate>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={roleColors[user.role?.name] || "secondary"}>
            {user.role?.name || "Unknown"}
          </Badge>
          <PinStatusBadge user={user} />
        </div>
        <Switch
          checked={user.isActive}
          onCheckedChange={() => handleToggleActive(user)}
          disabled={user.id === currentUser?.id}
          title={
            user.id === currentUser?.id ? "Cannot deactivate yourself" : undefined
          }
        />
      </div>
    </div>
  );

  if (isLoading && !usersData) return <PageLoader />;
  if (error)

    return <PageError message="Failed to load users" onRetry={refetch} />;

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <PageHeader
        title="User Management"
        description="Manage system users and their roles"
        actions={
          <PermissionButton
            permission="users.create"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add User
          </PermissionButton>
        }
      />
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Users ({pagination?.total || 0})
            </CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!users.length ? (
            <EmptyState
              icon={UserCog}
              title="No users found"
              description={
                search
                  ? "Try adjusting your search"
                  : "Get started by adding a new user"
              }
              action={
                !search && (
                  <PermissionButton
                    permission="users.create"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add User
                  </PermissionButton>
                )
              }
            />
          ) : (
            <DataTable
              data={users}
              columns={columns}
              rowKey={(user) => user.id}
              renderMobileCard={renderMobileCard}
              isFetching={isFetching}
              pagination={{
                page,
                pageSize,
                total: pagination?.total ?? 0,
                onPageChange: setPage,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setPage(1);
                },
              }}
            />
          )}
        </CardContent>
      </Card>
      <UserFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        roles={roles || []}
        onSubmit={handleCreate}
        isLoading={createUser.isPending}
        title="Create User"
      />
      {selectedUser && (
        <UserFormDialog
          open={isEditOpen}
          onOpenChange={(open) => {
            setIsEditOpen(open);
            if (!open) setSelectedUser(null);
          }}
          roles={roles || []}
          user={selectedUser}
          onSubmit={handleUpdate}
          isLoading={updateUser.isPending}
          title="Edit User"
        />
      )}

      {selectedUser && (
        <PasswordDialog
          open={isPasswordOpen}
          onOpenChange={(open) => {
            setIsPasswordOpen(open);
            if (!open) setSelectedUser(null);
          }}
          userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
          onSubmit={handleChangePassword}
          isLoading={changePassword.isPending}
        />
      )}

      {selectedUser && (
        <PinDialog
          open={isPinOpen}
          onOpenChange={(open) => {
            setIsPinOpen(open);
            if (!open) setSelectedUser(null);
          }}
          userName={`${selectedUser.firstName} ${selectedUser.lastName}`}
          onSubmit={handleSetPin}
          isLoading={setUserPin.isPending}
        />
      )}
    </div>
  );
}

interface UserFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: { id: string; name: string }[];
  user?: User;
  onSubmit: (data: Partial<CreateUserRequest>) => void;
  isLoading: boolean;
  title: string;
}

function UserFormDialog({
  open,
  onOpenChange,
  roles,
  user,
  onSubmit,
  isLoading,
  title,
}: UserFormDialogProps) {
  const [formData, setFormData] = useState({
    username: user?.username || "",
    email: user?.email || "",
    password: "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
    roleId: user?.roleId || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (user) {
      const { password: _, username: __, ...updateData } = formData;
      onSubmit(updateData);
    } else {
      onSubmit(formData as CreateUserRequest);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) =>
                  setFormData({ ...formData, firstName: e.target.value })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) =>
                  setFormData({ ...formData, lastName: e.target.value })
                }
                required
              />
            </div>
          </div>
          {!user && (
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
          </div>
          {!user && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({ ...formData, password: e.target.value })
                }
                required
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              value={formData.roleId}
              onValueChange={(value) =>
                setFormData({ ...formData, roleId: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface PasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  onSubmit: (password: string) => void;
  isLoading: boolean;
}

function PasswordDialog({
  open,
  onOpenChange,
  userName,
  onSubmit,
  isLoading,
}: PasswordDialogProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");

      return;
    }
    onSubmit(password);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Set a new password for <strong>{userName}</strong>
          </p>
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Change Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface PinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  onSubmit: (pin: string) => void;
  isLoading: boolean;
}

function PinDialog({
  open,
  onOpenChange,
  userName,
  onSubmit,
  isLoading,
}: PinDialogProps) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin !== confirmPin) {
      toast.error("PINs do not match");

      return;
    }
    if (!/^\d{4,6}$/.test(pin)) {
      toast.error("PIN must be 4-6 digits");

      return;
    }
    onSubmit(pin);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Set PIN</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Set a PIN for <strong>{userName}</strong> to use for quick login
          </p>
          <div className="space-y-2">
            <Label htmlFor="newPin">PIN</Label>
            <Input
              id="newPin"
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="4-6 digits"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPin">Confirm PIN</Label>
            <Input
              id="confirmPin"
              type="password"
              inputMode="numeric"
              maxLength={6}
              placeholder="4-6 digits"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Saving..." : "Set PIN"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
